import React, { useEffect, useRef, useState } from 'react';

export interface ToolbarMenuItem {
    key: string;
    icon: React.ReactNode;
    label: string;
    active?: boolean;
    onSelect: () => void;
}

interface ToolbarMenuProps {
    title: string;
    // Shown on the button itself: the icon of whichever item is active, so
    // the bar still says what the cursor is on with the menu closed.
    trigger: React.ReactNode;
    active?: boolean;
    items: ToolbarMenuItem[];
    // For items that combine (bold with italic, say): picking one leaves
    // the menu open for the next.
    keepOpen?: boolean;
}

/** One toolbar button that opens a short list of related commands, for
 *  groups that would otherwise spend a button each on the bar. */
export const ToolbarMenu: React.FC<ToolbarMenuProps> = ({ title, trigger, active, items, keepOpen }) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const close = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    // Escape closes without touching the document, the way the editor's
    // other popups behave.
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open]);

    return (
        <div className="toolbar-section toolbar-menu" ref={rootRef}>
            <button
                type="button"
                className={`toolbar-btn toolbar-menu-btn ${active ? 'active' : ''}`}
                onClick={() => setOpen((isOpen) => !isOpen)}
                title={title}
                aria-haspopup="menu"
                aria-expanded={open}
            >
                {trigger}
                <span className="toolbar-menu-caret">▾</span>
            </button>
            {open && (
                <div className="toolbar-menu-popup" role="menu">
                    {items.map((item) => (
                        <button
                            type="button"
                            key={item.key}
                            role="menuitem"
                            className={`toolbar-menu-option ${item.active ? 'active' : ''}`}
                            onClick={() => {
                                item.onSelect();
                                if (!keepOpen) setOpen(false);
                            }}
                        >
                            <span className="toolbar-menu-icon">{item.icon}</span>
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
